/**
 * @file modelTraining.ts
 * @brief "Use my data to improve models" (ADR-048 Part 2, web + Mac): what the
 *        member's toggle shows, the save error, and the activity-log line.
 *
 * @details
 * Wording always comes from the server (`wording` on GET), so a text change is
 * one backend edit plus a new version; the client sends back the `version` it
 * showed. While `companyManaged` is true the toggle is disabled, shown off,
 * with `managedByCompanyText`. A member's PUT true then gets
 * 409 MANAGED_BY_COMPANY: show the server message, keep the toggle off.
 *
 * Pure (no imports) so node:test can load it.
 *
 * @references
 * - BACKEND/src/features/auth/modelTrainingConsent.mjs
 * - docs/decisions/ADR-048-model-training-consent.md
 *
 * @author DES (prepared with Claude)
 * @date 20-10-2026
 */

export interface ModelTrainingWording {
  version: string;
  title: string;
  body: string;
  privacyUrl: string;
  managedByCompanyText: string;
}

export interface ModelTrainingState {
  optIn: boolean;
  effective: boolean;
  companyManaged: boolean;
  wording: ModelTrainingWording;
}

export interface CompanyModelTrainingState {
  allowMembers: boolean;
  wording: { title: string; body: string; privacyUrl: string };
}

/** What the member's switch shows. */
export function memberToggleView(state: Pick<ModelTrainingState, "optIn" | "companyManaged" | "wording">): {
  checked: boolean;
  disabled: boolean;
  caption: string | null;
} {
  if (state.companyManaged) {
    return { checked: false, disabled: true, caption: state.wording.managedByCompanyText };
  }
  return { checked: state.optIn, disabled: false, caption: null };
}

/** Toast text for a failed save; the server's sentence when it sent one. */
export function saveErrorMessage(err: { code?: string; message?: string } | null | undefined): string {
  const msg = err?.message?.trim();
  if (err?.code === "MANAGED_BY_COMPANY") {
    return msg || "Managed by your company.";
  }
  return msg || "Please try again.";
}

/** Activity log line for `company.model_training_changed`. */
export function describeModelTrainingChange(details: Record<string, unknown> | null | undefined): string {
  const on = details?.allowMembers;
  if (on === true) return "Allowed members to share data for model improvement";
  if (on === false) return "Stopped members sharing data for model improvement";
  return "Changed the data sharing setting";
}
